import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api.service';
import { Router } from '@angular/router';
import { CookieService } from '../../shared/services/cookie.service';
import { secureStorage } from '../../shared/services/secure-storage.service';
import { StorageService } from '../../shared/services/storage.service';
import { LoaderService } from '../../shared/services/loader.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  selector: 'app-address',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './address.component.html',
  styleUrl: './address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<{
    flatHouseNo: string;
    locality: string;
    pin: string;
    state: string;
    city: string;
    saveAs: string;
    receiverName: string;
    receiverPhone: string;
  }>();
  @Output() cancel = new EventEmitter<void>();

  @Input() type: any;
  @Input() editingAddress: any = null;
  @Input() isEditMode: boolean = false;

  address = {
    flatHouseNo: '',
    locality: '',
    pin: '',
    state: '',
    city: '',
    saveAs: 'home',
    receiverName: '',
    receiverPhone: '',
  };
  showAddAddressSection = true; // or false
  isValid = false;
  sub: Subscription = new Subscription();
  // You might also need:
  Sidefilter = false;
  addressForm!: FormGroup;
  stateCode: string = '';
  showSaveAsSection: boolean = false;
  userData: any;
  isPincodeLoading = false;
  pincodeError = '';
  autoFilledCity = false;
  autoFilledState = false;
  coordinates: any;
  curentRout: any;
  private lastFetchedPincode: string = ''; // Track last fetched pincode to prevent duplicates
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private storageService: StorageService,
    private ssrcookieService: CookieService,
    private localstorage: secureStorage,
    private loaderService: LoaderService,
    private route: Router,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.modalService.openModal('address-popup', () => this.onCancel());
    if (isPlatformBrowser(this.platformId)) {
      this.curentRout = this.route.url;

      try {
        const userDataString = localStorage.getItem('userData1');
        if (userDataString) {
          if (userDataString.startsWith('U2FsdGVkX1')) {
            this.userData = {};
          } else {
            this.userData = JSON.parse(userDataString);
          }
        } else {
          this.userData = {};
        }
      } catch (error) {
        console.error('Error parsing userData1 from localStorage:', error);
        this.userData = {};
        // this.notificationService.error(
        //   'Failed to load user data. Please log in again.',
        // );
      }
      this.initializeForm();

      // If in edit mode, populate form with existing data
      if (this.isEditMode && this.editingAddress) {
        this.populateFormWithExistingData();
      }
    }
  }

  initializeForm(): void {
    // Prevent duplicate initialization - unsubscribe existing subscriptions first
    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = new Subscription();
    }

    this.addressForm = this.fb.group({
      flatHouseNo: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(150),
        ],
      ],
      locality: ['', [Validators.required, Validators.minLength(2)]],
      pin: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      state: ['', [Validators.required, Validators.minLength(2)]],
      city: ['', [Validators.required, Validators.minLength(2)]],
      saveAs: ['home'],
      receiverName: [''],
      receiverPhone: ['', [Validators.pattern(/^[0-9]{10}$/)]],
      companyName: [''],
      saveAsName: [''],
    });

    // Subscribe to saveAs changes to conditionally add/remove validators for saveAsName
    this.addressForm
      .get('saveAs')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((saveAsValue: string) => {
        const saveAsNameControl = this.addressForm.get('saveAsName');
        if (saveAsValue === 'others') {
          // When 'others' is selected, make saveAsName required
          saveAsNameControl?.setValidators([
            Validators.required,
            Validators.minLength(2),
          ]);
        } else {
          // When 'home' or 'work' is selected, remove validators
          saveAsNameControl?.clearValidators();
          saveAsNameControl?.setValue(''); // Clear the value as well
        }
        saveAsNameControl?.updateValueAndValidity();
        this.cdr.markForCheck();
      });

    // Subscribe to pincode changes
    this.addressForm
      .get('pin')
      ?.valueChanges.pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged(), // Only emit if value has changed
        takeUntil(this.destroy$),
      )
      .subscribe((pincode: string) => {
        this.onPincodeChange(pincode);
      });

    // Subscribe to city and state changes to track manual edits
    this.addressForm
      .get('city')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (
          this.autoFilledCity &&
          value !== this.addressForm.get('city')?.value
        ) {
          this.autoFilledCity = false;
        }
      });

    this.addressForm
      .get('state')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        if (
          this.autoFilledState &&
          value !== this.addressForm.get('state')?.value
        ) {
          this.autoFilledState = false;
        }
      });
  }

  onPincodeChange(pincode: string): void {
    // Clear previous error
    this.pincodeError = '';

    // Reset auto-filled flags when pincode changes
    this.autoFilledCity = false;
    this.autoFilledState = false;

    // Reset last fetched pincode if it's different
    if (this.lastFetchedPincode !== pincode) {
      this.lastFetchedPincode = '';
    }

    // Check if pincode is valid (6 digits)
    if (pincode && pincode.length === 6 && /^[0-9]{6}$/.test(pincode)) {
      this.getAddressDataByPincode(pincode);
    } else if (pincode && pincode.length > 0) {
      // Clear city and state if pincode is invalid
      this.addressForm.patchValue({
        city: '',
        state: '',
      });
    }
  }

  onPincodeInput(event: any): void {
    // Only handle input sanitization here
    // The valueChanges subscription with debounceTime will handle the API call
    const pincode = event.target.value;

    // Remove non-numeric characters
    const numericOnly = pincode.replace(/[^0-9]/g, '');

    if (pincode !== numericOnly) {
      this.addressForm.get('pin')?.setValue(numericOnly, { emitEvent: true });
      event.target.value = numericOnly;
    }

    // Note: Don't call onPincodeChange here - let the valueChanges subscription handle it
    // to avoid duplicate API calls
  }

  toggleSaveAsSection(): void {
    this.showSaveAsSection = !this.showSaveAsSection;
  }

  useCurrentLocation(): void {
    // Implement geolocation logic here
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Use reverse geocoding service
          // cto get address from coordinates

          this.coordinates = position.coords;

          this.getGeoLocation();
          // You would typically call a geocoding service here
          // to convert coordinates to address and populate the form
        },
        (error) => {
          console.error('Error getting location:', error);
          // this.notificationService.error(
          //   'Failed to get your location. Please enable location access.',
          // );
        },
      );
        } else {
          console.error('Geolocation is not supported by this browser.');
          // this.notificationService.error(
          //   'Geolocation is not supported by your browser.',
          // );
    }
  }

  onSave(): void {
    if (this.addressForm.valid) {
      const formData = {
        ...this.addressForm.value,
        // Add any additional processing here
        timestamp: new Date().toISOString(),
      };

      let payload: any = {
        address_1: formData.flatHouseNo,
        address_2: '',
        address_3: '',
        state: formData.state,
        city: formData.city,
        land_mark: formData.locality,
        pincode: formData.pin,
        phone_no: this.userData?.phone_number,
        is_gstin_no: false,
        gstin_number: '',
        address_type:
          formData.saveAs === 'others' ? formData.saveAsName : formData.saveAs,
        address_type_name:
          this.type === 'billing' ? 'Billing Address' : 'Permanent Address',
        state_code: this.stateCode,
      };

      // Add type-specific fields
      if (this.type === 'shipping') {
        payload.receiver_name = formData.receiverName;
        payload.receiver_phone_no = formData.receiverPhone;
        payload.is_save_ship_default = true;
      } else if (this.type === 'billing') {
        payload.company_name = formData.companyName;
        payload.is_save_bill_default = true;
      } else if (this.type === 'address') {
        payload.receiver_name = formData.receiverName;
        payload.receiver_phone_no = formData.receiverPhone;
        payload.is_save_other_default = true;
      }

      // If in edit mode, add the id for update and use update API
      if (this.isEditMode && this.editingAddress) {
        // Use 'id' field for updateAdress API (as per other components)
        payload.id = this.editingAddress.id;
        // Also include address_id for compatibility
        payload.address_id = this.editingAddress.id;

        // For billing address, ensure company_name is included
        if (this.type === 'billing') {
          payload.company_name = formData.companyName;
          // Set billing_address flag for update
          payload.billing_address = true;
        } else {
          payload.receiver_name = formData.receiverName;
          payload.receiver_phone_no = formData.receiverPhone;
          // Set shipping_address flag for update
          payload.shipping_address = this.type === 'shipping' ? true : '';
        }
      }

      // Use update API if in edit mode, otherwise use add API
      // const apiCall = this.isEditMode && this.editingAddress
      //   ? this.apiService.updateAdress(payload)
      //   : this.apiService.addAddress(payload);
      const apiCall = this.apiService.addAddress(payload);

      this.loaderService.show();
      apiCall.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.addressForm.reset();
            setTimeout(() => {
              this.getUserDetails();
            }, 1000);
            this.showSaveAsSection = false;
            this.autoFilledCity = false;
            this.autoFilledState = false;
            this.pincodeError = '';
            this.isPincodeLoading = false;
            this.close.emit();
            this.save.emit(formData); // Emit the save event with form data
            this.loaderService.hide();

            this.cdr.markForCheck();
          } else {
            this.loaderService.hide();
            // this.notificationService.error(
            //   res?.data?.msg ||
            //     (this.isEditMode
            //       ? 'Failed to update address'
            //       : 'Failed to add address'),
            // );
            this.cdr.markForCheck();
          }
        },
        error: (err: any) => {
          console.error(
            this.isEditMode
              ? 'Error updating address:'
              : 'Error adding address:',
            err,
          );
          this.loaderService.hide();
          // this.notificationService.error(
          //   err?.error?.data?.msg ||
          //     (this.isEditMode
          //       ? 'Error updating address. Please try again.'
          //       : 'Error adding address. Please try again.'),
          // );
          this.cdr.markForCheck();
        },
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.addressForm);
      // this.notificationService.error(
      //   'Please fill all required fields correctly',
      // );
    }
  }

  onCancel(): void {
    this.addressForm.reset();
    this.showSaveAsSection = false;
    this.autoFilledCity = false;
    this.autoFilledState = false;
    this.pincodeError = '';
    this.isPincodeLoading = false;
    this.modalService.closeModal('address-popup');
    // this.notificationService.info('Address form cancelled');
    this.close.emit();
  }

  closeModal(): void {
    this.modalService.closeModal('address-popup');
    if (this.addressForm.dirty) {
      // this.notificationService.info('Changes discarded');
    }
    this.close.emit();
  }

  // Mobile-specific method for better touch handling
  onBackdropClick(event: Event): void {
    // Only close if clicking directly on backdrop, not on modal content
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Getter for form validation status
  get isFormValid(): boolean {
    return this.addressForm.valid;
  }

  // Individual field getters for easier template access
  get flatHouseNo() {
    return this.addressForm.get('flatHouseNo');
  }
  get locality() {
    return this.addressForm.get('locality');
  }
  get pin() {
    return this.addressForm.get('pin');
  }
  get state() {
    return this.addressForm.get('state');
  }
  get city() {
    return this.addressForm.get('city');
  }
  get saveAs() {
    return this.addressForm.get('saveAs');
  }
  get receiverName() {
    return this.addressForm.get('receiverName');
  }
  get receiverPhone() {
    return this.addressForm.get('receiverPhone');
  }
  get companyName() {
    return this.addressForm.get('companyName');
  }

  onClose(): void {
    this.close.emit();
  }

  selectSaveAs(type: string) {
    // logic to save type (home, work, etc.)
  }

  getUserDetails(): void {
    this.loaderService.show();
    const payload = {};
    this.apiService
      .getUserDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Update localStorage and cookies with latest data
          localStorage.setItem('userData1', JSON.stringify(res?.data));
          this.ssrcookieService.setItem('eol_user', JSON.stringify(res?.data));
          this.userData = res?.data;
          this.loaderService.hide();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.loaderService.hide();
          // this.notificationService.error(
          //   err.error?.data?.msg || 'Failed to fetch user details',
          // );
          this.cdr.markForCheck();
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  getAddressDataByPincode(pincode: string): void {
    // Prevent duplicate API calls for the same pincode
    if (this.isPincodeLoading || this.lastFetchedPincode === pincode) {
      return;
    }

    this.loaderService.show();
    this.isPincodeLoading = true;
    this.pincodeError = '';
    this.lastFetchedPincode = pincode;

    const payload = {
      dropdown_type: 'get_data_by_pincode',
      search_pincode: pincode,
    };

    this.apiService
      .dataBypincode(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isPincodeLoading = false;

          if (res && res?.data && res?.data?.data_by_pincode?.length > 0) {
            const addressData = res?.data?.data_by_pincode[0]; // Get the first result

            // Auto-fill city and state - handle different possible field names
            const city = addressData.city_name || '';
            const state = addressData.state_name || '';

            this.stateCode = addressData.state_code || '';

            this.addressForm.patchValue({
              city: city,
              state: state,
            });

            // Set auto-filled flags
            this.autoFilledCity = !!city;
            this.autoFilledState = !!state;

            // Clear any previous errors
            this.pincodeError = '';

            // Show success notification
            this.cdr.markForCheck();
          } else {
            this.pincodeError = 'No address found for this pincode';
            // this.notificationService.info('No address found for this pincode');
            // Clear city and state if no data found
            this.addressForm.patchValue({
              city: '',
              state: '',
            });
            this.autoFilledCity = false;
            this.autoFilledState = false;
            this.cdr.markForCheck();
          }
          this.loaderService.hide();
        },
        error: (err) => {
          this.isPincodeLoading = false;
          console.error('Failed to fetch address data by pincode', err);
          this.pincodeError = 'Failed to fetch address data. Please try again.';
          // this.notificationService.error(
          //   err.error?.data?.msg ||
          //     'Failed to fetch address data. Please try again.',
          // );

          // Clear city and state on error
          this.addressForm.patchValue({
            city: '',
            state: '',
          });
          this.autoFilledCity = false;
          this.autoFilledState = false;
          this.loaderService.hide();
          this.cdr.markForCheck();
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  getGeoLocation(): void {
    this.loaderService.show();

    const payload = {
      latitude: this.coordinates?.latitude,
      longitude: this.coordinates?.longitude,
    };

    this.apiService
      .geoLocation(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res && res?.data && res?.data?.data_by_pincode?.length > 0) {
            const addressData = res?.data?.data_by_pincode[0]; // Get the first result

            // Auto-fill city and state - handle different possible field names
            const city = addressData.city_name || '';
            const state = addressData.state_name || '';

            // Truncate full address to 150 characters max
            let fullAddress = addressData.full_address || '';
            if (fullAddress.length > 150) {
              fullAddress = fullAddress.substring(0, 150);
              // this.notificationService.info(
              //   'Address was truncated to 150 characters. Please review and edit if needed.',
              // );
            }

            this.addressForm.patchValue({
              city: city,
              state: state,
              flatHouseNo: fullAddress,
              locality: addressData.taluka_name,
              pin: addressData.pincode,
              saveAs: 'home',
              receiverName: '',
              receiverPhone: '',
            });

            // Mark flatHouseNo as touched to show validation if needed
            this.addressForm.get('flatHouseNo')?.markAsTouched();
            this.addressForm.get('locality')?.markAsTouched();

            this.autoFilledCity = !!city;
            this.autoFilledState = !!state;
            this.pincodeError = '';
            this.loaderService.hide();
            this.cdr.markForCheck();
          } else {
            this.pincodeError = 'No address found for this pincode';
            // this.notificationService.info(
            //   res.data?.msg || 'No address found for this location',
            // );
            // Clear city and state if no data found
            this.addressForm.patchValue({
              city: '',
              state: '',
            });
            this.autoFilledCity = false;
            this.autoFilledState = false;
            this.cdr.markForCheck();
          }
          this.loaderService.hide();
        },
        error: (err) => {
          this.loaderService.hide();
            // this.notificationService.error(
            //   err.error?.data?.msg ||
            //     'Failed to fetch location data. Please try again.',
            // );
          this.cdr.markForCheck();
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  private populateFormWithExistingData(): void {
    if (this.editingAddress) {
      // Check if address_type is a custom value (not in standard list: home, work, etc.)
      const standardTypes = ['home', 'work', 'others'];
      const addressType = this.editingAddress.address_type || 'home';
      const isCustomType = !standardTypes.includes(addressType.toLowerCase());

      // Truncate address if longer than 150 characters
      let flatHouseNo = this.editingAddress.address_1 || '';
      if (flatHouseNo.length > 150) {
        flatHouseNo = flatHouseNo.substring(0, 150);
        // this.notificationService.info(
        //   'Address was truncated to 150 characters. Please review and edit if needed.',
        // );
      }

      const formData = {
        flatHouseNo: flatHouseNo,
        locality: this.editingAddress.land_mark || '',
        pin: this.editingAddress.pincode || '',
        state: this.editingAddress.state || '',
        city: this.editingAddress.city || '',
        saveAs: isCustomType ? 'others' : addressType || 'home',
        saveAsName: isCustomType ? addressType : '',
        receiverName: this.editingAddress.receiver_name || '',
        receiverPhone: this.editingAddress.receiver_phone_no || '',
        companyName: this.editingAddress.company_name || '',
      };

      this.addressForm.patchValue(formData);

      // Mark fields as touched so validation will display if needed
      this.addressForm.get('flatHouseNo')?.markAsTouched();

      // this.notificationService.info('Editing existing address');
      this.cdr.markForCheck();
    }
  }

  // Method to handle text input - trim leading spaces
  onTextInput(fieldName: string, event: any): void {
    const value = event.target.value;
    // Remove leading spaces
    const trimmedValue = value.replace(/^\s+/, '');

    if (value !== trimmedValue) {
      this.addressForm
        .get(fieldName)
        ?.setValue(trimmedValue, { emitEvent: false });
      // Update the input field value
      event.target.value = trimmedValue;
    }
  }

  // Method to handle numeric input - remove all spaces
  onNumericInput(fieldName: string, event: any): void {
    const value = event.target.value;
    // Remove all spaces
    const noSpacesValue = value.replace(/\s/g, '');

    if (value !== noSpacesValue) {
      this.addressForm
        .get(fieldName)
        ?.setValue(noSpacesValue, { emitEvent: false });
      // Update the input field value
      event.target.value = noSpacesValue;
    }
  }

  // Method to handle blur event - trim trailing spaces from text fields
  onTextBlur(fieldName: string, event: any): void {
    const value = event.target.value;
    // Trim both leading and trailing spaces
    const trimmedValue = value.trim();

    if (value !== trimmedValue) {
      this.addressForm.get(fieldName)?.setValue(trimmedValue);
    }
  }

  ngOnDestroy() {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Unsubscribe from all subscriptions
    if (this.sub) {
      this.sub.unsubscribe();
    }

    // Reset loading states
    this.isPincodeLoading = false;
    this.modalService.closeModal('address-popup');
    this.lastFetchedPincode = '';

    // Clean up the form to prevent memory leaks
    if (this.addressForm) {
      this.addressForm.reset();
    }
  }
}
